import { useState, type ReactNode } from 'react';
import type { PhotoItem } from '@/types/odoo';
import { isPdfPhoto } from '@/utils/photoFiles';
import { PhotoLightbox } from './PhotoLightbox';
import { PdfPlanThumb, usePdfPlanViewer } from './PdfPlanPreview';

/**
 * The files uploaded for installers on a werf ("Bestanden voor installateurs",
 * the installer's "Verlegschema's").
 *
 * The plans upload takes any file type, so the list is mixed. Documents and
 * photos are shown as two groups. A photo shows a thumbnail and opens in
 * `PhotoLightbox`, where next/prev walks every photo in the list and the wheel
 * or a pinch zooms. A PDF shows page 1 as its thumbnail and opens its own pages
 * in the same viewer. Anything else (Word, …) keeps its link: the photo proxy
 * serves it inline, so the browser opens or downloads it as it always did.
 *
 * HEIC is not in the image list on purpose: plans are uploaded without the
 * browser-side transcode photos get, and only Safari can draw a HEIC `<img>`.
 */
const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;

export function isPreviewableImage(item: { key: string }): boolean {
  return IMAGE_EXTENSIONS.test(item.key);
}

/** The key's last segment without the leading upload timestamp ("1774362023707-"). */
export function planDisplayName(key: string): string {
  const raw = key.split('/').pop() || key;
  return raw.replace(/^\d{10,}-/, '');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function PlanFileList({
  plans,
  linkLabel,
  linkClassName,
  viewLabel,
  showMeta = false,
  renderExtra,
}: {
  plans: PhotoItem[];
  /** Label of the open/download link a non-image file gets. */
  linkLabel: string;
  linkClassName: string;
  /** When set, a photo's link becomes a button with this label that opens the viewer. */
  viewLabel?: string;
  /** Show size and date under the name. */
  showMeta?: boolean;
  /** Extra controls at the end of a row (the office's delete). */
  renderExtra?: (plan: PhotoItem) => ReactNode;
}) {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const pdf = usePdfPlanViewer();

  const documents = plans.filter((p) => !isPreviewableImage(p));
  const photos = plans.filter(isPreviewableImage);
  const viewerPhotos = photos.map((p) => ({ ...p, filename: planDisplayName(p.key) }));
  const openViewer = (key: string) => setViewerIndex(photos.findIndex((p) => p.key === key));

  const renderRow = (plan: PhotoItem) => {
    const name = planDisplayName(plan.key);
    const isImage = isPreviewableImage(plan);
    const isPdf = isPdfPhoto(plan);
    const open = isImage ? () => openViewer(plan.key) : isPdf ? () => pdf.open(plan, name) : null;
    return (
      <div key={plan.key} className="flex items-center gap-3 rounded-lg border border-gray-100 p-3">
        {isImage ? (
          <button
            type="button"
            onClick={() => openViewer(plan.key)}
            className="shrink-0 cursor-pointer"
            aria-label={`Foto ${name} openen`}
          >
            <img
              src={plan.url}
              alt={name}
              loading="lazy"
              className="w-12 h-12 object-cover rounded-lg border border-gray-200 hover:border-td-blue transition-colors"
            />
          </button>
        ) : isPdf ? (
          <PdfPlanThumb url={plan.url} name={name} loading={pdf.loadingKey === plan.key} onOpen={() => pdf.open(plan, name)} />
        ) : (
          <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )}
        <div className="flex-1 min-w-0">
          {open ? (
            <button
              type="button"
              onClick={open}
              className="block max-w-full text-left text-sm font-semibold text-td-navy truncate hover:text-td-blue hover:underline cursor-pointer"
            >
              {name}
            </button>
          ) : (
            <p className="text-sm font-semibold text-td-navy truncate">{name}</p>
          )}
          {showMeta && (
            <p className="text-xs text-td-muted">
              {formatSize(plan.size)} &middot; {formatDate(plan.lastModified)}
            </p>
          )}
          {pdf.errorKey === plan.key && (
            <p className="text-xs text-red-500">
              PDF kon niet getoond worden.{' '}
              <a href={plan.url} target="_blank" rel="noopener noreferrer" className="underline">
                Open in nieuw tabblad
              </a>
            </p>
          )}
        </div>
        {open && viewLabel ? (
          <button type="button" onClick={open} className={linkClassName}>
            {viewLabel}
          </button>
        ) : (
          <a href={plan.url} target="_blank" rel="noopener noreferrer" className={linkClassName}>
            {linkLabel}
          </a>
        )}
        {renderExtra?.(plan)}
      </div>
    );
  };

  // Headings only when both kinds are present; a single kind needs no label.
  const grouped = documents.length > 0 && photos.length > 0;
  const group = (label: string, items: PhotoItem[]) =>
    items.length > 0 && (
      <div className="space-y-2">
        {grouped && <p className="text-[10px] font-bold text-td-muted uppercase tracking-wide pt-1">{label}</p>}
        {items.map(renderRow)}
      </div>
    );

  return (
    <>
      {group('Documenten', documents)}
      {group("Foto's", photos)}
      {viewerIndex !== null && viewerIndex >= 0 && (
        <PhotoLightbox photos={viewerPhotos} initialIndex={viewerIndex} onClose={() => setViewerIndex(null)} />
      )}
      {pdf.pages && pdf.pages.length > 0 && (
        <PhotoLightbox photos={pdf.pages} initialIndex={0} onClose={pdf.close} />
      )}
    </>
  );
}
